import { FormEvent, useState } from "react";
import {
  ChatWidgetInput,
  ChatWidgetText,
  ChatWidgetValues,
  FormWidget,
} from "../../../Helper/ChatWidget";
import {
  Button,
  Card,
  ChatWidgetAnswer,
  cx,
  Foot,
  Receipt,
} from "../ChatWidgetPrimitives";
import "./FormCard.scss";

const isFilled = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value.length > 0 : !!value?.trim();

/** "Identity provider: Okta · How many people sign in? About 40" */
export const formAnswerLabel = (
  inputs: ChatWidgetInput[],
  values: ChatWidgetValues,
) =>
  inputs
    .filter((input) => isFilled(values[input.name]))
    .map((input) => {
      const value = values[input.name];
      const text = Array.isArray(value) ? value.join(", ") : value;
      // "What happens instead? Spinner forever", not "…instead?: Spinner".
      return /[?:!.]$/.test(input.label.trim())
        ? `${input.label.trim()} ${text}`
        : `${input.label}: ${text}`;
    })
    .join(" · ");

const Field = ({
  id,
  input,
  value,
  onChange,
  text,
}: {
  id: string;
  input: ChatWidgetInput;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
  text: ChatWidgetText;
}) => {
  const placeholder =
    input.placeholder ?? (input.required ? undefined : text("widgetOptional"));
  if (input.type === "chips") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div
        className="cw-form-chips"
        role="group"
        aria-labelledby={`${id}-label`}
      >
        {(input.options ?? []).map((option) => {
          const isOn = selected.includes(option);
          return (
            <button
              type="button"
              key={option}
              aria-pressed={isOn}
              className={cx("cw-form-chip", isOn && "cw-form-chip--on")}
              onClick={() =>
                onChange(
                  isOn
                    ? selected.filter((entry) => entry !== option)
                    : [...selected, option],
                )
              }
            >
              {option}
            </button>
          );
        })}
      </div>
    );
  }
  if (input.type === "select") {
    return (
      <select
        id={id}
        className="cw-form-control"
        value={typeof value === "string" ? value : ""}
        required={input.required}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="" disabled>
          {input.placeholder ?? ""}
        </option>
        {(input.options ?? []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  if (input.type === "textarea") {
    return (
      <textarea
        id={id}
        className="cw-form-control"
        value={typeof value === "string" ? value : ""}
        placeholder={placeholder}
        required={input.required}
        rows={2}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  return (
    <input
      id={id}
      className="cw-form-control"
      type={input.type}
      value={typeof value === "string" ? value : ""}
      placeholder={placeholder}
      required={input.required}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

/** 03 Form: a grouped field block (iOS-settings style) with one Send. */
export const FormCard = ({
  widget,
  onAnswer,
  text,
}: {
  widget: FormWidget;
  onAnswer: ChatWidgetAnswer;
  text: ChatWidgetText;
}) => {
  const [values, setValues] = useState<ChatWidgetValues>({});
  const inputs = widget.inputs ?? [];

  if (widget.response) {
    return (
      <Receipt title={text("widgetSent")} detail={widget.response.label} />
    );
  }

  // Every required field, and never an empty form.
  const canSubmit =
    inputs.every((input) => !input.required || isFilled(values[input.name])) &&
    inputs.some((input) => isFilled(values[input.name]));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    onAnswer("submit", values, formAnswerLabel(inputs, values));
  };

  return (
    <Card className="cw-form" label={widget.title}>
      <form onSubmit={submit} noValidate>
        {(widget.title || widget.subtitle) && (
          <div className="cw-form-head">
            {widget.title && <div className="cw-h">{widget.title}</div>}
            {widget.subtitle && (
              <div className="cw-sm cw-muted">{widget.subtitle}</div>
            )}
          </div>
        )}
        <div className="cw-form-group">
          {inputs.map((input) => {
            const id = `cw-${widget.id}-${input.name}`;
            return (
              <div className="cw-form-field" key={input.name}>
                <label
                  id={`${id}-label`}
                  htmlFor={input.type === "chips" ? undefined : id}
                >
                  {input.label}
                  {input.required && (
                    <span
                      className="cw-form-required"
                      aria-label={text("widgetRequired")}
                    >
                      {" *"}
                    </span>
                  )}
                </label>
                <Field
                  id={id}
                  input={input}
                  value={values[input.name]}
                  text={text}
                  onChange={(value) =>
                    setValues((current) => ({
                      ...current,
                      [input.name]: value,
                    }))
                  }
                />
              </div>
            );
          })}
        </div>
        <Foot>
          <Button type="submit" disabled={!canSubmit}>
            {widget.submitLabel || text("widgetSubmit")}
          </Button>
        </Foot>
      </form>
    </Card>
  );
};
